import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import VideocamIcon from "@mui/icons-material/VideocamOutlined";
import { useEffect, useState } from "react";
import type { VideoCallSeatSummary } from "@shared/types/foyer";
import { foyerApi, type LicenseRecord } from "../http/foyer";
import { useCan } from "../auth/AuthContext";

/** The `product` value that models the pool. Mirrors the server's
 *  `VIDEO_CALL_PRODUCT` (src/server/db/models/License.ts). */
export const VIDEO_CALL_PRODUCT = "video-call";

interface Props {
  premiseId: string;
  /**
   * The premise's licenses, as already loaded by the parent card. Passed in
   * rather than re-fetched so the panel and the table below it can never
   * disagree about which `video-call` rows exist - and so a new array identity
   * (the parent's post-mutation refresh) re-reads the seat summary, which any
   * license change can move.
   */
  licenses: LicenseRecord[] | null;
  /** Re-load the parent's licenses (and, through them, this panel). */
  onChanged: () => Promise<void>;
}

/**
 * The premise's video-call seat POOL: how many residents calling was bought
 * for, how many hold a seat, how many are left.
 *
 * Lives inside the Licenses card because the pool *is* a License row
 * (`product: "video-call"`, `quantity` = seats, null = site license) - the same
 * row the table below renders. Two things make it worth its own panel rather
 * than leaving it to the generic license dialog:
 *
 *   1. The generic dialog can only state what was *bought*. The number an
 *      operator actually needs before promising a family a video call is
 *      "how many are LEFT", which comes from a different endpoint
 *      (`/video-call/seats/summary`) because it counts assigned residents.
 *   2. `product` is free-text, so buying calling here otherwise means knowing
 *      to type exactly `video-call`. A typo doesn't fail - it silently creates
 *      a license that entitles nobody.
 *
 * Sizing the pool is gated on `canManageLicenses` (admin-only, i.e. Nenda
 * staff) exactly like every other license mutation; the counts are readable by
 * any logged-in user, matching the licenses summary, because the operator who
 * assigns seats needs to see the ceiling they are working under.
 */
export default function VideoCallSeatPoolPanel({
  premiseId,
  licenses,
  onChanged,
}: Props) {
  const canManage = useCan("canManageLicenses");
  const [summary, setSummary] = useState<VideoCallSeatSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const sum = await foyerApi.videoCallSeatSummary(premiseId);
        if (!alive) return;
        // Defensive: a mocked test fetch might return a non-object.
        setSummary(sum && typeof sum === "object" ? sum : null);
        setErr(null);
      } catch (e) {
        if (!alive) return;
        setErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      alive = false;
    };
    // `licenses` is the parent's refresh signal - see the prop docs.
  }, [premiseId, licenses]);

  // Every video-call row, and the subset the pool actually counts. A premise
  // may legitimately hold several (two purchase orders), and a row parked as
  // `pending`/`cancelled` contributes nothing - both facts change what we can
  // offer to edit, so keep them apart.
  const rows = (licenses ?? []).filter((l) => l.product === VIDEO_CALL_PRODUCT);
  const activeRows = rows.filter((l) => l.status === "active");
  // Only offer in-place editing when there is exactly one row to edit. With
  // several, "change the pool size" has no single answer, so we send the admin
  // to the table rather than silently picking a row.
  const editable = rows.length === 1 ? rows[0] : null;

  return (
    <Box
      sx={{
        mb: 3,
        p: 2,
        borderRadius: 2,
        bgcolor: "action.hover",
      }}
      data-testid="video-call-pool"
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
        >
          <VideocamIcon fontSize="small" color="action" />
          <Typography variant="subtitle2">Video calling seats</Typography>
          {summary && <SeatChips summary={summary} />}
        </Stack>

        {canManage &&
          (editable || rows.length === 0 ? (
            <Button
              size="small"
              variant={
                summary?.poolSeats || summary?.unlimited ? "text" : "outlined"
              }
              onClick={() => setDialogOpen(true)}
              data-testid="video-call-pool-edit"
            >
              {rows.length === 0 ? "Buy seats" : "Change pool size"}
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {rows.length} video-call licenses — edit them in the table below.
            </Typography>
          ))}
      </Stack>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
      >
        {summaryCopy(summary, rows.length, activeRows.length)}
      </Typography>

      {err && (
        <Typography variant="caption" color="error" sx={{ display: "block" }}>
          {err}
        </Typography>
      )}

      {dialogOpen && (
        <PoolDialog
          premiseId={premiseId}
          record={editable}
          assignedFromPool={summary?.assignedFromPool ?? 0}
          onClose={() => setDialogOpen(false)}
          onSaved={async () => {
            setDialogOpen(false);
            await onChanged();
          }}
        />
      )}
    </Box>
  );
}

/** "3 / 10 assigned" + the family-funded seats that sit outside the pool. */
function SeatChips({ summary }: { summary: VideoCallSeatSummary }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <Chip
        size="small"
        variant="outlined"
        color={
          summary.unlimited
            ? "success"
            : summary.hasFreeSeats
              ? "primary"
              : "error"
        }
        label={
          summary.unlimited
            ? `${summary.assignedFromPool} assigned · unlimited`
            : `${summary.assignedFromPool} / ${summary.poolSeats} assigned`
        }
        data-testid="video-call-pool-summary"
      />
      {!summary.unlimited && summary.poolSeats > 0 && (
        <Chip
          size="small"
          variant="outlined"
          label={`${summary.freeSeats} free`}
          data-testid="video-call-pool-free"
        />
      )}
      {summary.familySeats > 0 && (
        <Tooltip title="Bought by relatives in the family portal - paid separately, so they don't use up pool seats.">
          <Chip
            size="small"
            variant="outlined"
            label={`+${summary.familySeats} family-funded`}
            data-testid="video-call-pool-family"
          />
        </Tooltip>
      )}
    </Stack>
  );
}

/**
 * One line of plain language under the chips. The empty and the
 * bought-but-not-active states are the two an operator can otherwise stare at
 * for a long time: both look like "video calling is broken" from the TV, and
 * neither is fixed on the TV.
 */
function summaryCopy(
  summary: VideoCallSeatSummary | null,
  rowCount: number,
  activeRowCount: number
): string {
  if (!summary) return "Loading…";
  if (summary.unlimited) {
    return "Site license: every resident may be given video calling.";
  }
  if (rowCount > 0 && activeRowCount === 0) {
    return summary.familySeats > 0
      ? "No video-call license is active, so the pool is empty. Only family-funded seats are working."
      : "This site has a video-call license, but it isn't active — the pool is empty until its status is set to active.";
  }
  if (summary.poolSeats === 0) {
    return summary.familySeats > 0
      ? "No seats bought for this site. Only relatives who bought a seat themselves can call."
      : "No seats bought for this site, so no resident can be given video calling.";
  }
  if (!summary.hasFreeSeats) {
    return "Every seat is assigned. Buy more, or release one, before another resident can be given calling.";
  }
  return "Assign seats to residents on the Residents page.";
}

/**
 * Buy or resize the pool. Writes the same License row the generic dialog
 * writes, with `product` pinned to `video-call` and the quantity framed as
 * residents rather than screens.
 */
function PoolDialog({
  premiseId,
  record,
  assignedFromPool,
  onClose,
  onSaved,
}: {
  premiseId: string;
  record: LicenseRecord | null;
  assignedFromPool: number;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [unlimited, setUnlimited] = useState(
    record != null && record.quantity == null
  );
  const [seats, setSeats] = useState(
    record?.quantity != null ? String(record.quantity) : ""
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const parsed = Number(seats);
  const seatsValid =
    unlimited ||
    (seats.trim() !== "" && Number.isInteger(parsed) && parsed >= 0);
  // Shrinking below what's already handed out is allowed on purpose: the store
  // never strands an assigned resident (VC2-4), it just refuses NEW draws. Say
  // so, so nobody assumes seats were revoked - or that the save failed.
  const shrinksBelowAssigned =
    !unlimited && seatsValid && parsed < assignedFromPool;

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const quantity = unlimited ? null : parsed;
      if (record) {
        // Re-activating is part of resizing: an admin who sets a seat count on
        // a parked license means for those seats to exist.
        await foyerApi.updateLicense(record.id, {
          product: VIDEO_CALL_PRODUCT,
          quantity,
          status: "active",
        });
      } else {
        await foyerApi.createLicense({
          premiseId,
          product: VIDEO_CALL_PRODUCT,
          status: "active",
          quantity,
          notes: null,
        });
      }
      await onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {record ? "Video calling seats" : "Buy video calling seats"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            One seat entitles one resident to video calling. Seats follow the
            resident, not the room, and are freed automatically on discharge.
          </Typography>
          <TextField
            label="Seats"
            type="number"
            value={unlimited ? "" : seats}
            onChange={(e) => setSeats(e.target.value)}
            disabled={unlimited}
            size="small"
            autoFocus
            inputProps={{
              min: 0,
              step: 1,
              "data-testid": "video-call-pool-seats",
            }}
            helperText={
              unlimited
                ? "Unlimited - no seat count."
                : "How many residents calling is bought for."
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={unlimited}
                onChange={(e) => setUnlimited(e.target.checked)}
                inputProps={
                  { "data-testid": "video-call-pool-unlimited" } as Record<
                    string,
                    string
                  >
                }
              />
            }
            label="Site license (unlimited seats)"
          />
          {shrinksBelowAssigned && (
            <Alert severity="info" data-testid="video-call-pool-shrink-warning">
              {assignedFromPool} resident{assignedFromPool === 1 ? "" : "s"}{" "}
              already hold a seat. They keep it — but no new seat can be
              assigned until the pool is bigger than that.
            </Alert>
          )}
          {err && (
            <Typography variant="caption" color="error">
              {err}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={busy || !seatsValid}
          data-testid="video-call-pool-save"
        >
          {busy ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
